import { useQuery } from "@apollo/client";
import {
  CNC_GET_USERS,
  RESET_USER_PASSWORD,
  CNC_INVITE_USER,
  CNC_DELETE_USER,
  CNC_EDIT_USER,
  CHANGE_USER_PASSWORD,
  type CncGetUsersData,
  type CncGetUsersVariables,
  type CncInviteUserData,
  type CncInviteUserVariables,
  type CncDeleteUserData,
  type CncDeleteUserVariables,
  type CncEditUserData,
  type CncEditUserVariables,
  type ResetUserPasswordData,
  type ResetUserPasswordVariables,
  type ChangeUserPasswordData,
  type ChangeUserPasswordVariables,
} from "../CustomerProvisioningApi";
import React, { useContext, useEffect, useState } from "react";
import { showToast } from "../../Forms/Dialogs/Toast/Toast";
import "./ManageDomainUsers.scss";
import { CNC_GET_PROFILES } from "../../AppSettings/Permissions/PermissionsApi";
import UsersList, {
  type UserProfile,
  type UsersListUser,
} from "../../AppSettings/UserManagement/UsersList/UsersList";
import {
  TextButton,
  useMutationWrapper,
  ModalContext,
  ModalButtonsContainer,
  showDialog,
  showErrorDialog,
} from "@SeemplicityDev/shared";

// `cnc_profiles` shares the CoreProfileFields fragment used by `cnc_users`
// profiles; only the fields read below are typed.
interface CncProfilesNode {
  id: string;
  name: string;
  scopes: { edges: unknown[] };
  scope_groups: { edges: unknown[] };
}

interface CncGetProfilesData {
  cnc_profiles: { edges: { node: CncProfilesNode }[] };
}

// The role option surfaced by the user-actions form (value/label pair).
interface RoleOption {
  value?: string;
  label?: string;
}

// Boundary payload shapes produced by the user-actions form. The canonical
// submit handler signature is `(Record<string, unknown>) => Promise<unknown>`
// (UserSubmitHandler); these describe the concrete object after the cast.
interface CncAdditionPayload {
  email: string;
  firstName: string;
  lastName?: string;
  role?: RoleOption;
  profiles?: UserProfile[];
  withoutEmail?: boolean;
  password?: string;
  visible?: boolean;
}

interface CncEditingPayload {
  id: string;
  firstName: string;
  lastName: string;
  role?: RoleOption;
  profiles?: UserProfile[];
  visible?: boolean;
}

export interface ManageDomainUsersProps {
  accountUuid: string;
  domains: string[];
  ssoEnforced?: boolean;
}

const ManageDomainUsers: React.FC<ManageDomainUsersProps> = ({
  accountUuid,
  domains,
  ssoEnforced,
}) => {
  const { loading, data: usersData } = useQuery<
    CncGetUsersData,
    CncGetUsersVariables
  >(CNC_GET_USERS, {
    fetchPolicy: "no-cache",
    variables: {
      account_uuid: accountUuid,
    },
  });
  const { data: profilesData } = useQuery<
    CncGetProfilesData,
    CncGetUsersVariables
  >(CNC_GET_PROFILES, {
    fetchPolicy: "no-cache",
    variables: {
      account_uuid: accountUuid,
    },
  });
  const [resetUserPassword] = useMutationWrapper(
    RESET_USER_PASSWORD
  ) as unknown as [
    (args: {
      variables: ResetUserPasswordVariables;
    }) => Promise<{ data?: ResetUserPasswordData }>,
  ];
  const [inviteUser] = useMutationWrapper(CNC_INVITE_USER, {
    refetchQueries: ["cnc_users"],
  }) as unknown as [
    (args: {
      variables: CncInviteUserVariables;
    }) => Promise<{ data?: CncInviteUserData }>,
  ];
  const [deleteUser] = useMutationWrapper(CNC_DELETE_USER, {
    refetchQueries: ["cnc_users"],
  }) as unknown as [
    (args: {
      variables: CncDeleteUserVariables;
    }) => Promise<{ data?: CncDeleteUserData }>,
  ];
  const [editUser] = useMutationWrapper(CNC_EDIT_USER, {
    refetchQueries: ["cnc_users"],
  }) as unknown as [
    (args: {
      variables: CncEditUserVariables;
    }) => Promise<{ data?: CncEditUserData }>,
  ];
  const [changeUserPassword] = useMutationWrapper(CHANGE_USER_PASSWORD, {
    refetchQueries: ["cnc_organizations"],
  }) as unknown as [
    (args: {
      variables: ChangeUserPasswordVariables;
    }) => Promise<{ data?: ChangeUserPasswordData }>,
  ];
  const [profilesSuggestions, setProfilesSuggestions] = useState<UserProfile[]>(
    []
  );
  const [users, setUsers] = useState<UsersListUser[]>([]);
  const { closeModal } = useContext(ModalContext);

  const onChangePassword = (
    newPassword: string,
    userID: string,
    name: string
  ) => {
    return changeUserPassword({
      variables: {
        account_uuid: accountUuid,
        user_id: userID,
        new_password: newPassword,
      },
    })
      .then((res) => {
        if (!!res.data?.set_user_password?.ok) {
          closeModal();
          showToast({
            description: (
              <div className={"modal-text"}>
                The Password for{" "}
                <span className={"modal-live-text"}>{name}</span> was changed
                successfully
              </div>
            ),
          });
        } else {
          showErrorDialog({ message: res.data?.set_user_password?.msg });
        }
      })
      .catch((err: Error) => {
        showErrorDialog({ message: err.message });
      });
  };

  const onResetPassword = (userID: string, email: string) => {
    resetUserPassword({
      variables: { account_uuid: accountUuid, user_id: userID },
    })
      .then((res) => {
        if (!!res?.data?.cnc_reset_user_password?.ok) {
          closeModal();
          showToast({
            description: (
              <div className={"modal-text"}>
                A temporary password was sent to
                <span className={"modal-live-text"}> {email}</span>
              </div>
            ),
          });
        } else {
          showErrorDialog({ message: res.data?.cnc_reset_user_password?.msg });
        }
      })
      .catch((err: Error) => {
        showErrorDialog({ message: err.message });
      });
  };

  const createUser = (formData: Record<string, unknown>) => {
    // Boundary cast: the canonical opaque submit payload is read as the known
    // addition shape produced by the user-actions form.
    const {
      email,
      firstName,
      lastName,
      role,
      profiles,
      withoutEmail,
      password,
      visible,
    } = formData as unknown as CncAdditionPayload;

    return inviteUser({
      variables: {
        account_uuid: accountUuid,
        user: {
          first_name: firstName,
          last_name: lastName ? lastName : "",
          email: email,
          visible: visible,
          role_id: role?.value,
          profile_ids: Array.isArray(profiles)
            ? // el.id is `string | undefined` on UserProfile; the original maps
              // it through unchanged, so narrow the array to the input shape.
              (profiles.map((el) => el.id) as string[])
            : undefined,
          password: password,
        },
        send_email: !withoutEmail,
      },
    })
      .then((res) => {
        if (res.data?.cnc_invite_user?.ok) {
          closeModal();
          showDialog({
            title: "User Added",
            message: (
              <div className={"modal-text"}>
                <div>
                  The user{" "}
                  <span className={"modal-live-text"}>
                    {`${firstName} ${lastName ? lastName : ""}`}
                  </span>
                  {withoutEmail
                    ? " has been added successfully, and a temporary password has been set."
                    : " has been added successfully."}
                </div>
                {!withoutEmail && (
                  <span>
                    A temporary password was sent to{" "}
                    <span className={"modal-live-text"}>{email}</span>
                  </span>
                )}
              </div>
            ),
            buttons: [
              {
                "data-testid": "close-addition-success",
                label: "Close",
              },
            ],
          });
        } else {
          showErrorDialog({ message: res.data?.cnc_invite_user?.msg });
        }
      })
      .catch((error: Error) => showErrorDialog({ message: error.message }));
  };

  const handleEditing = (formData: Record<string, unknown>) => {
    // Boundary cast: the canonical opaque submit payload is read as the known
    // editing shape produced by the user-actions form.
    const { id, firstName, lastName, role, profiles, visible } =
      formData as unknown as CncEditingPayload;

    return editUser({
      variables: {
        account_uuid: accountUuid,
        user_id: id,
        first_name: firstName,
        last_name: lastName,
        role_id: role?.value,
        profile_ids: Array.isArray(profiles)
          ? profiles?.reduce<string[]>((acc, profile) => {
              if (!!profile?.id) {
                acc.push(profile.id);
              }

              return acc;
            }, [])
          : [],
        visible: visible,
      },
    })
      .then((res) => {
        if (res.data?.cnc_change_user_fields?.ok) {
          closeModal();
          showToast({ message: `${res.data?.cnc_change_user_fields?.msg}` });
        } else {
          showErrorDialog({ message: res.data?.cnc_change_user_fields?.msg });
        }
      })
      .catch((error: Error) => showErrorDialog({ message: error.message }));
  };

  const handleDeletion = (id: string, email: string, name: string) => {
    deleteUser({
      variables: { user_id: id, account_uuid: accountUuid },
    })
      .then((res) => {
        if (res.data?.cnc_delete_user?.ok) {
          showToast({
            message: (
              <div className={"modal-text"}>
                The User
                <span className={"modal-live-text"}>
                  {` ${name || ""} (${email}) `}
                </span>
                was deleted successfully
              </div>
            ),
          });
        } else {
          showErrorDialog({ message: res.data?.cnc_delete_user?.msg });
        }
      })
      .catch((error: Error) => {
        showErrorDialog({ message: error.message });
      });
  };

  useEffect(() => {
    if (!!usersData) {
      const formattedUsers = usersData?.cnc_users?.edges?.map((user) => {
        const userProfiles: UserProfile[] =
          user?.node?.profiles?.edges?.map((profile) => {
            return {
              id: profile.node.id,
              name: profile.node.name,
              hasScopes:
                profile?.node?.scopes?.edges?.length ||
                profile?.node?.scope_groups?.edges?.length,
            };
          }) ?? [];
        const rawSamlGroupsProfiles = user?.node?.saml_group_profiles || [];
        const userSamlGroupsProfiles: UserProfile[] =
          rawSamlGroupsProfiles?.map((profile) => {
            return {
              name: `${profile?.profile?.name} (${profile?.saml_group})`,
              id: profile?.profile?.id,
              readOnly: true,
            };
          });

        return {
          email: user?.node?.email,
          firstName: user?.node?.first_name,
          lastName: user?.node?.last_name,
          login: user?.node?.last_login,
          disabled: !Boolean(user?.node?.last_login),
          id: user?.node?.id,
          role: user?.node?.role,
          profiles: userProfiles?.concat(userSamlGroupsProfiles),
          userIdentityProvider: user?.node?.identity_provider_source,
        };
      });

      setUsers(formattedUsers ?? []);
    }
  }, [usersData]);

  useEffect(() => {
    if (!!profilesData) {
      const formattedProfiles = profilesData?.cnc_profiles?.edges?.map(
        (profile) => {
          return {
            id: profile?.node?.id,
            name: profile?.node?.name,
            hasScopes:
              profile?.node?.scopes?.edges?.length ||
              profile?.node?.scope_groups?.edges?.length,
          };
        }
      );

      setProfilesSuggestions(formattedProfiles ?? []);
    }
  }, [profilesData]);

  return (
    <div className={"manage-domain-users-wrap"}>
      <div className="modal-scroll-area">
        <UsersList
          users={users}
          loading={loading}
          profilesSuggestions={profilesSuggestions}
          isCNC={true}
          allowedDomains={domains}
          onAdd={createUser}
          onEdit={handleEditing}
          onChangePassword={onChangePassword}
          onReInvite={onResetPassword}
          onDelete={handleDeletion}
          ssoEnforced={ssoEnforced}
        />
      </div>

      <ModalButtonsContainer centerButtons>
        <TextButton
          type="button"
          data-testid={""}
          label={"Close"}
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            closeModal();
          }}
        />
      </ModalButtonsContainer>
    </div>
  );
};
export default ManageDomainUsers;
